import type { CSSProperties } from "react";
import { memo } from "react";
import {
  ORGANIC_STEM_PATH,
  buildOrganicEyePath,
  resolveOrganicAvatar,
} from "./organic.js";

export interface OrganicBotAvatarProps {
  color: string;
  identity?: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
  isWorking?: boolean;
  pokeable?: boolean;
  onPoke?: () => void;
}

/**
 * Standard vector Bot avatar component for the 8 unified organic shapes.
 * Directly renders the 200x200 standard canvas geometry with native tilt & eyes.
 */
export const OrganicBotAvatar = memo(function OrganicBotAvatar({
  color,
  identity = "",
  size = 48,
  className,
  style,
  isWorking = false,
  pokeable = false,
  onPoke,
}: OrganicBotAvatarProps) {
  const resolved = resolveOrganicAvatar(color, identity);

  if (resolved.kind === "image" && resolved.imageUrl) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          overflow: "hidden",
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          userSelect: "none",
          ...style,
        }}
      >
        <img
          src={resolved.imageUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  const { shape, eyeColor, tilt, d } = resolved;
  const leftEyeX = 100 - shape.eyeGap;
  const rightEyeX = 100 + shape.eyeGap;

  return (
    <div
      className={className}
      onClick={pokeable ? onPoke : undefined}
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        userSelect: "none",
        cursor: pokeable ? "pointer" : undefined,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        aria-hidden="true"
        style={{ overflow: "visible" }}
      >
        <g transform={`rotate(${tilt} 100 100)`}>
          {shape.hasStem ? (
            <path
              d={ORGANIC_STEM_PATH}
              transform="translate(100, 42.5)"
              fill="#231F20"
            />
          ) : null}
          <path d={d} fill={resolved.color} />
          <g fill={eyeColor}>
            <path d={buildOrganicEyePath(leftEyeX, shape.eyeY, 0, 1.0)} />
            <path d={buildOrganicEyePath(rightEyeX, shape.eyeY, 0, 1.0)} />
          </g>
        </g>
      </svg>
    </div>
  );
});
